import { classNames } from "primereact/utils";
import styles from "./BirthDateField.module.scss";
import { Controller, useFormContext } from "react-hook-form";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { useState, useEffect } from "react";
import { useAppContext } from "../../../../../AppContext";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IBirthDateFieldProps {
  className?: string;
  name: string;
  placeholder?: string;
  label?: string;
  required?: boolean;
  width?: string;
  disabled?: boolean;
}

export const BirthDateField = ({
  className,
  name,
  placeholder = "дд/мм/гггг",
  label,
  required = false,
  width,
  disabled,
}: IBirthDateFieldProps) => {
  const { control } = useFormContext();
  const { isDesktopOrTablet } = useAppContext();
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");

  if (!width) {
    width = isDesktopOrTablet ? "600px" : dvSize("80dvw");
  }

  const formatBirthDate = (input: string): string => {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    if (digits.length === 0) return "";

    let day = digits.substring(0, 2);
    let month = digits.substring(2, 4);
    let year = digits.substring(4, 8);

    // Validate and auto-correct day
    if (day.length > 0) {
      let dayNum = parseInt(day);
      if (dayNum > 31) {
        day = "31";
      } else if (day.length === 2 && dayNum === 0) {
        day = "01";
      }
    }

    // Validate and auto-correct month
    if (month.length > 0) {
      let monthNum = parseInt(month);
      if (monthNum > 12) {
        month = "12";
      } else if (month.length === 2 && monthNum === 0) {
        month = "01";
      }

      // Adjust day based on month
      if (month.length === 2 && day.length === 2) {
        const dayNum = parseInt(day);
        const monthsWith30Days = [4, 6, 9, 11];

        if (monthsWith30Days.includes(monthNum) && dayNum > 30) {
          day = "30";
        } else if (monthNum === 2) {
          // February - check for leap year if we have the year
          if (year.length === 4) {
            const yearNum = parseInt(year);
            const isLeapYear =
              (yearNum % 4 === 0 && yearNum % 100 !== 0) ||
              yearNum % 400 === 0;
            const maxFebDays = isLeapYear ? 29 : 28;
            if (dayNum > maxFebDays) {
              day = maxFebDays.toString().padStart(2, "0");
            }
          } else if (dayNum > 29) {
            day = "29";
          }
        }
      }
    }

    // Validate year - cannot be greater than current year
    if (year.length > 0) {
      const currentYear = new Date().getFullYear();
      if (year.length === 4) {
        const yearNum = parseInt(year);
        if (yearNum > currentYear) {
          year = currentYear.toString();
        }
      }
    }

    // Build formatted string with slashes
    let formatted = day;
    if (digits.length >= 2) {
      formatted += "/" + month;
    }
    if (digits.length >= 4) {
      formatted += "/" + year;
    }

    return formatted;
  };

  const parseToDate = (formattedString: string): Date | null => {
    if (!formattedString) return null;

    const parts = formattedString.split("/");
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JS months are 0-indexed
    const year = parseInt(parts[2]);

    if (
      isNaN(day) ||
      isNaN(month) ||
      isNaN(year) ||
      parts[2].length !== 4
    ) {
      return null;
    }

    const date = new Date(year, month, day);

    // Validate the date is real (handles invalid dates like Feb 31)
    if (
      date.getDate() !== day ||
      date.getMonth() !== month ||
      date.getFullYear() !== year
    ) {
      return null;
    }

    return date;
  };

  const formatDateToString = (date: Date | null): string => {
    if (!date) return "";

    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear().toString();

    return `${day}/${month}/${year}`;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        // Sync inputValue with field.value when field changes externally (e.g., form reset or initial load)
        useEffect(() => {
          if (field.value && !isFocused) {
            setInputValue(formatDateToString(field.value));
          } else if (!field.value && !isFocused) {
            setInputValue("");
          }
        }, [field.value, isFocused]);

        return (
          <div
            style={{ width: width }}
            className={classNames(className, styles.root, {
              [styles.root_disabled]: disabled,
              [styles.root_error]: !!fieldState.error?.message,
            })}
          >
            <RenderWhen condition={!!label}>
              <div
                className={classNames(styles.root_label, {
                  [styles.root_label_interacted_with]:
                    isFocused || !!field.value || fieldState.isTouched,
                  [styles.root_label_error]: !!fieldState.error?.message,
                })}
              >
                {label}
                {required ? "*" : ""}
              </div>
            </RenderWhen>
            <div className={styles.root_input_container}>
              <input
                type="text"
                className={styles.root_input}
                placeholder={placeholder}
                value={inputValue}
                disabled={disabled}
                onFocus={() => setIsFocused(true)}
                onBlur={() => {
                  setIsFocused(false);
                  field.onBlur();
                  // Update form field with parsed date on blur
                  const dateValue = parseToDate(inputValue);
                  field.onChange(dateValue);
                }}
                onClick={(e) => {
                  // Always move cursor to the end
                  const input = e.currentTarget;
                  input.selectionStart = input.value.length;
                  input.selectionEnd = input.value.length;
                }}
                onSelect={(e) => {
                  // Prevent text selection, always keep cursor at the end
                  const input = e.currentTarget;
                  input.selectionStart = input.value.length;
                  input.selectionEnd = input.value.length;
                }}
                onKeyDown={(e) => {
                  // Prevent arrow key navigation
                  if (
                    e.key === "ArrowLeft" ||
                    e.key === "ArrowRight" ||
                    e.key === "ArrowUp" ||
                    e.key === "ArrowDown" ||
                    e.key === "Home" ||
                    e.key === "End"
                  ) {
                    e.preventDefault();
                    return;
                  }

                  // Smart backspace: if cursor is at the end and last char is "/",
                  // remove both "/" and the digit before it
                  if (e.key === "Backspace") {
                    const input = e.currentTarget;
                    const cursorPos = input.selectionStart || 0;
                    const value = inputValue;

                    // Only allow backspace at the end
                    if (cursorPos !== value.length) {
                      e.preventDefault();
                      return;
                    }

                    // Check if last character is "/"
                    if (
                      value.length > 0 &&
                      value[value.length - 1] === "/"
                    ) {
                      e.preventDefault();
                      // Remove the "/" and the character before it
                      const newValue = value.slice(0, -2);
                      setInputValue(newValue);

                      // Clear the form field since we don't have a complete date
                      field.onChange(null);
                    }
                  }

                  // Prevent Delete key
                  if (e.key === "Delete") {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const formattedValue = formatBirthDate(e.target.value);
                  setInputValue(formattedValue);

                  // Only update the form field if we have a complete valid date
                  const dateValue = parseToDate(formattedValue);
                  if (dateValue) {
                    field.onChange(dateValue);
                  }
                }}
                maxLength={10} // dd/mm/yyyy = 10 characters
              />
            </div>
            <RenderWhen condition={!!fieldState.error?.message}>
              <div className={styles.root_error_message}>
                {fieldState.error?.message}
              </div>
            </RenderWhen>
          </div>
        );
      }}
    />
  );
};
